import {
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionTypeBase,
  ValidationErrors,
} from 'platform/components';
import {Grid, GridItem, Show, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';
import {object} from 'yup';

import {useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {
  GetSaleVehicleApiResponse,
  useDeleteLeasingAndCreditCompanyMutation,
  useEnableLeasingAndCreditCompanyMutation,
  useLeasingAndCreditCompanyMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useToggle, yupString} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../../utils/handleApiError';

interface VehicleFinancingCardFormProps extends TestIdProps {
  /**
   * Resolved sale vehicle. The parent renders this component only once the query has settled with
   * data, so every read below is safe without a nil guard. Keep it non-optional: React Compiler
   * lifts property reads out of callbacks into render-time dependency checks, so a nullable value
   * deref'd inside a handler would crash during render, where JSX-level guards cannot help.
   */
  saleVehicle: GetSaleVehicleApiResponse;
  institutionOptions: OptionTypeBase<string>[];
  isReadonly?: boolean;
  isWithSubmit?: boolean;
}

interface VehicleFinancingFormValues {
  leasingAndCreditCompanyCodeId: string | null;
  financingContractNumber: string | null;
  endOfFinancing: string | null;
  financingNote: string | null;
}

const SAVE_DEBOUNCE_TIMEOUT = 500;

export function VehicleFinancingCardForm(props: VehicleFinancingCardFormProps) {
  const vehicleId = props.saleVehicle.vehicleId;

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const [
    hasVehicleEnableLeasingAndCreditCompany,
    hasVehicleDisableLeasingAndCreditCompany,
    hasVehicleSetLeasingAndCreditCompany,
    hasVehicleSetFleetInsurance,
    hasVehicleUpdateStorageSpace,
  ] = usePermissions({
    permissionKeys: [
      'vehicleEnableLeasingAndCreditCompany',
      'vehicleDisableLeasingAndCreditCompany',
      'vehicleSetLeasingAndCreditCompany',
      'vehicleSetFleetInsurance',
      'vehicleUpdateStorageSpace',
    ],
    scopes: {
      vehicleEnableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle.branchId,
      },
      vehicleDisableLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle.branchId,
      },
      vehicleSetLeasingAndCreditCompany: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle.branchId,
      },
      vehicleSetFleetInsurance: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle.branchId,
      },
      vehicleUpdateStorageSpace: {
        participation: vehicleParticipation,
        branchId: props.saleVehicle.branchId,
      },
    },
  });

  const canEditSaleVehicle =
    hasVehicleEnableLeasingAndCreditCompany &&
    hasVehicleDisableLeasingAndCreditCompany &&
    hasVehicleSetLeasingAndCreditCompany &&
    hasVehicleSetFleetInsurance &&
    hasVehicleUpdateStorageSpace;

  const [enableLeasingAndCreditCompany] = useEnableLeasingAndCreditCompanyMutation();
  const [deleteLeasingAndCreditCompany] = useDeleteLeasingAndCreditCompanyMutation();
  const [leasingAndCreditCompany] = useLeasingAndCreditCompanyMutation();

  const isEnabled = props.saleVehicle.leasingAndCreditCompany;
  const [isExpanded, toggleExpanded] = useToggle(isEnabled);

  const saveTimeout = useRef<ReturnType<typeof setTimeout>>(null);

  const saveFormAndToggle = (formApi: UseFormReturn<VehicleFinancingFormValues>) => {
    toggleExpanded();
    formApi.setValue('leasingAndCreditCompanyCodeId', null);
    formApi.setValue('endOfFinancing', null);
    formApi.setValue('financingContractNumber', null);
    formApi.setValue('financingNote', null);
  };

  const handleToggle = (formApi: UseFormReturn<VehicleFinancingFormValues>) => {
    (isExpanded
      ? deleteLeasingAndCreditCompany({vehicleId})
      : enableLeasingAndCreditCompany({vehicleId})
    )
      .unwrap()
      .then(() => (!isExpanded ? saveFormAndToggle(formApi) : toggleExpanded()))
      .catch(handleApiError);
  };

  const saveForm = (values: VehicleFinancingFormValues) => {
    const codeId = values.leasingAndCreditCompanyCodeId;

    // The financing institution is mandatory once financing is enabled. Bail out instead of
    // sending a null codeId, which the API rejects.
    if (isNil(codeId)) {
      return Promise.resolve();
    }

    return leasingAndCreditCompany({
      vehicleId,
      codeId,
      body: {
        endOfFinancing: values.endOfFinancing,
        financingContractNumber: values.financingContractNumber,
        note: values.financingNote,
      },
    })
      .unwrap()
      .then(() => {
        props.isWithSubmit && closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const onSubmit: FormSubmitHandler<VehicleFinancingFormValues> = (values) =>
    match<[boolean, boolean], Promise<void>>([isExpanded, isEnabled])
      // Financing was switched off - remove it and close the dialog.
      .with([false, Pattern.any], () =>
        deleteLeasingAndCreditCompany({vehicleId})
          .unwrap()
          .then(closeCurrentDialog)
          .catch(handleApiError)
      )
      // Financing was just switched on - enable it first, then persist the form.
      .with([true, false], () =>
        enableLeasingAndCreditCompany({vehicleId})
          .unwrap()
          .then(() => saveForm(values))
          .catch(handleApiError)
      )
      // Financing is already enabled - only persist the form.
      .with([true, true], () => saveForm(values))
      .exhaustive();

  const handleChange = (
    values: VehicleFinancingFormValues,
    setErrors: (errors: ValidationErrors | null) => void
  ) => {
    if (isExpanded && isNil(values.leasingAndCreditCompanyCodeId)) {
      setErrors([
        {
          name: 'leasingAndCreditCompanyCodeId',
          message: i18n.t('general.labels.required'),
        },
      ]);

      return;
    }

    setErrors(null);

    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    saveTimeout.current = setTimeout(() => {
      saveForm(values);
    }, SAVE_DEBOUNCE_TIMEOUT);
  };

  const defaultValues: Partial<VehicleFinancingFormValues> = {
    leasingAndCreditCompanyCodeId: props.saleVehicle.leasingAndCreditCompanyCodeId ?? undefined,
    endOfFinancing: props.saleVehicle.endOfFinancing ?? undefined,
    financingContractNumber: props.saleVehicle.financingContractNumber ?? undefined,
    financingNote: props.saleVehicle.financingNote ?? undefined,
  };

  const isDisabled = props.isReadonly || !canEditSaleVehicle;

  return (
    <Form<VehicleFinancingFormValues>
      onChange={!props.isWithSubmit ? handleChange : undefined}
      onSubmit={props.isWithSubmit ? onSubmit : undefined}
      defaultValues={defaultValues}
      schema={schema(isExpanded)}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Card
            variant="inlineGrey"
            title={i18n.t('entity.checkout.labels.financing')}
            control={{
              type: 'switch',
              onChange: () => handleToggle(formApi),
              value: isExpanded,
              isDisabled,
            }}
            onExpandButtonClick={() =>
              isDisabled || props.isWithSubmit ? undefined : handleToggle(formApi)
            }
            isExpanded={isExpanded}
          >
            <Grid columns={[1, 2, 4]}>
              <FormField
                label={i18n.t('entity.checkout.labels.financingInstitution')}
                name="leasingAndCreditCompanyCodeId"
                type="choice"
                options={props.institutionOptions}
                control={control}
                isNotClearable
                isRequired
                data-testid={suffixTestId('financingInstitution', props)}
                isDisabled={isDisabled}
              />
              <FormField
                label={i18n.t('entity.checkout.labels.endOfFinancing')}
                name="endOfFinancing"
                type="apiDate"
                control={control}
                data-testid={suffixTestId('endOfFinancing', props)}
                isDisabled={isDisabled}
              />
              <FormField
                label={i18n.t('entity.checkout.labels.contractNumber')}
                name="financingContractNumber"
                type="text"
                control={control}
                data-testid={suffixTestId('contractNumber', props)}
                isDisabled={isDisabled}
              />
              <GridItem span={[1, 2, 4]}>
                <FormField
                  label={i18n.t('general.labels.note')}
                  name="financingNote"
                  type="textarea"
                  control={control}
                  data-testid={suffixTestId('note', props)}
                  isDisabled={isDisabled}
                />
              </GridItem>
            </Grid>
          </Card>
          <Show when={props.isWithSubmit}>
            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discard')}
                onClick={closeCurrentDialog}
                variant="secondary"
                data-testid={suffixTestId('discard', props)}
                isDisabled={isDisabled}
              />
              <FormButton
                title={i18n.t('general.actions.save')}
                type="submit"
                control={control}
                data-testid={suffixTestId('save', props)}
                isDisabled={isDisabled}
              />
            </ButtonGroup>
          </Show>
        </VStack>
      )}
    </Form>
  );
}

const schema = (isExpanded: boolean) =>
  object({
    leasingAndCreditCompanyCodeId: isExpanded ? yupString.required() : yupString.nullable(),
    endOfFinancing: yupString,
    financingContractNumber: yupString,
    financingNote: yupString,
  });
